import { useState, Fragment, useRef, useEffect, forwardRef } from "react";
import { Link } from "react-router-dom";
import { SURVEY_YEARS, isEmpty } from "../../assets/constants";
import { Badge, Select, Divider, Space, createStyles, Group, Textarea, Text, Button, Slider, RangeSlider, Switch, NumberInput, ActionIcon, Alert, Modal, Code, Container } from "@mantine/core";
import { useForm } from "@mantine/form";
import { requireAccountToAccessSandbox, alphabet } from "../../assets/constants";
import { useJuneProvider } from "../../contexts/analytics.context";
import { IconPlus, IconX, IconPointFilled, IconGripVertical } from "@tabler/icons-react";
import DragNDrop from "../drag-n-drop/drag-n-drop.component";
import ResponseConditioningModal from "../condition-modal/condition-modal.component";

const defaultYear = 2023;
const firstYear = Math.min(...SURVEY_YEARS);
const lastYear = Math.max(...SURVEY_YEARS);

const sliderYearMarks = [
    { value: 2000, label: '2000' },
    { value: 2005, label: '2005' },
    { value: 2010, label: '2010' },
    { value: 2015, label: '2015' },
    { value: 2020, label: '2020' },
];

const useStyles = createStyles((theme) => ({

    root: {
        position: 'relative',
    },

    input: {
        height: 'auto',
        paddingTop: 25,
        paddingBottom: 7,
        lineHeight: '1em'
    },

    label: {
        position: 'absolute',
        pointerEvents: 'none',
        fontSize: theme.fontSizes.xs,
        paddingLeft: theme.spacing.sm,
        paddingTop: 4,
        color: 'gray',
        zIndex: 1,
    },

    formWrapper: {
        width: '360px',
        overflowY: 'scroll',
        overflowX: 'hidden',
        height: 'calc(100vh - 115px)',
        direction: 'rtl',
        '&::-webkit-scrollbar': {
            backgroundColor: 'transparent',
            transform: 'translateY(-100px)',
            'width': '8px',
        },
        borderLeft: '2px solid #f1f1f1',
        '&:hover': {
            '&::-webkit-scrollbar-thumb': window.ontouchstart === undefined && {
                borderRadius: '8px',
                backgroundColor: 'rgba(0,0,0,0.5)',
            }
        },
        [theme.fn.smallerThan('sm')]: {
            margin: '0 auto',
            width: '100%',
            overflowY: 'initial',
            height: 'auto',
        },
    },

    innerForm: {
        position: 'relative',
        direction: 'ltr',
        width: '100%',
        padding: '0px 25px',
    },

    historyIcon: {
        width: '20px',
        height: '20px',
        color: theme.colors.dark[7],
    },


    inputWithRightSectionMargin: {
        '.mantine-Input-rightSection': {
            marginRight: '5px',
        }
    },

    infoIcon: {
        transform: 'translateY(2px)'
    },

    surveyFormInnerTitle: {
        fontWeight: 400,
    },

    modalContent: {
        zIndex: '99999',
    },

    conditioningButton: {
        width: '100%',
        fontWeight: 400,
        border: `1px solid  ${theme.colors.gray[3]}`,
    },

    conditioningContainer: {
        display: 'flex',
        background: '#f1f1f1',
        textOverflow: 'ellipsis',
        borderRadius: '16px',
        height: '40px',
        lineHeight: '40px',
    },

    modalButton: {
        width: '100%',
    },

    slider: {
        width: `calc(100% - 16px)`,
        margin: '0 auto',
    },

}));

const getAnswerErrors = (values) => {
    if (values.length < 2) return "Please enter at least 2 answers";
    let errors = [];
    values.forEach((answer, index) => {
        if (answer.length < 1) {
            errors[index] = "Please enter an answer";
        } else if (values.filter((v) => v === answer).length > 1) {
            errors[index] = "Please enter a unique answer";
        } else {
            errors[index] = null;
        }
    });
    // Set first "Please enter a unique answer" error to null
    const firstUniqueErrorIndex = errors.findIndex((e) => e === "Please enter a unique answer");
    if (firstUniqueErrorIndex > -1) {
        errors[firstUniqueErrorIndex] = null;
    }
    return errors;
};

const validateSurveyAnswers = (values) => {
    const errors = getAnswerErrors(values);
    const numErrors = errors.filter((e) => e !== null).length;
    if (numErrors > 0) {
        return 'Error';
    }
};

const getSubmitButtonText = (allowUserToRunSurvey, isPanel, surveyType, isFetching) => {
    if (!allowUserToRunSurvey) return 'Sign up to simulate';
    if (isFetching) return "";
    if (isPanel) return 'Simulate panel'
    if (surveyType !== 'MCQ') return 'Simulate study';
    return 'Simulate survey';
}

const DetailedSelect = forwardRef(({ label, description, labelImage, ...others }, ref) => (
    <Container px='xs' ref={ref} {...others}>
        <Group noWrap>
            <div>
                <Group spacing='xs'>
                    <Text size="sm" weight={500}>{label}</Text>
                    {labelImage && labelImage}
                </Group>
                <Space h={2} />
                <Text size="xs" weight={500} sx={{ wordBreak: 'initial' }}>{description}</Text>
            </div>
        </Group>
    </Container>
));

const mergeDictionaries = (dict1, dict2) => {
    let newDict = { ...dict1 };

    for (let key in dict1) {
        if (dict2.hasOwnProperty(key)) {
            newDict[key] = dict2[key];
        }
    }

    return newDict;
}

const getFunctionString = (surveyType) => {
    switch (surveyType) {
        case 'MCQ':
            return 'runSimulation';
        case 'GGS':
            return 'gaborGranger';
        // end
        default:
            return 'runSimulation';
    }
}

const convertCurrencyStringToNumber = (currencyString) => {
    // Remove any dollar signs or commas
    let cleanedString = currencyString.replace(/[\$,]/g, '');

    // Convert the cleaned string to a number
    let numberValue = parseFloat(cleanedString);

    return numberValue;
}

const SurveyForm = ({
    surveyResponse,
    setSurveyResponse,
    userSurveyHistory,
    setUserSurveyHistory,
    isRunningNewSurvey,
    setIsRunningNewSurvey,
    currentUser,
    numCreditsAvailable,
    updateCreditUsage,
}) => {

    const { classes } = useStyles();

    const [defaultFetchError, setDefaultFetchError] = useState(false);
    const [overCreditLimit, setOverCreditLimit] = useState(false);
    const [conditionModalOpen, setConditionModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);

    const questionRef = useRef(null);
    const answerRef = useRef(null);

    const analytics = useJuneProvider();


    const initialFormState = {
        surveyType: 'MCQ',
        surveyModel: 'GSS-v1.1',
        surveyQuestion: '',
        surveyAnswers: ['', ''],
        customQuestionsAndResponses: [],
        surveyDescriptions: [],
        isPanel: false,
        surveyYears: [defaultYear, defaultYear],
        questionType: 'product',
        surveyCountry: 'United States',
        product: '',

        // Gabor granger stuff
        gaborGrangerProductName: '',
        gaborGrangerProductDescription: '',
        gaborGrangerMinPrice: 10,
        gaborGrangerMaxPrice: 100,
    };

    const form = useForm({
        initialValues: { ...initialFormState },
        validate: {
            surveyQuestion: (value) => form.values.surveyType === 'MCQ' && value.length === 0 && 'Error',
            surveyAnswers: (value) => form.values.surveyType === 'MCQ' && validateSurveyAnswers(value),
            gaborGrangerProductName: (value) => form.values.surveyType === 'GGS' && value.length === 0 && 'Error',
            gaborGrangerProductDescription: (value) => form.values.surveyType === 'GGS' && value.length === 0 && 'Error',
        },
    });

    const numSurveysInBatch = form.values.surveyType === 'MCQ' ? (form.values.surveyYears[1] - form.values.surveyYears[0]) + 1 : 5;

    const removeSurveyItem = (fieldName, index) => {
        form.removeListItem(fieldName, index)
    }

    const getInputAutoFocus = (fieldName, index) => {
        const values = form.getInputProps(fieldName).value;
        const minLength = fieldName === 'surveyAnswers' ? 3 : 1;
        if (values.length < minLength) return false;
        const indexIsLast = index === values.length - 1;
        if (!indexIsLast) return false;
        const value = values[index];
        return value === '';
    }

    const highlightFormErrors = () => {
        if (form.values.surveyType === 'MCQ') {
            const answerErrors = getAnswerErrors(form.getInputProps('surveyAnswers').value);
            answerErrors.forEach((error, index) => {
                if (error !== null) form.setFieldError(`surveyAnswers.${index}`, error);
            });
            if (form.getInputProps('surveyQuestion').value.length === 0) {
                form.setFieldError('surveyQuestion', 'Please enter a question');
            }
        }
        if (form.values.surveyType === 'GGS') {
            if (form.getInputProps('gaborGrangerProductName').value.length === 0) {
                form.setFieldError('gaborGrangerProductName', 'Please enter a product name');
            }
            if (form.getInputProps('gaborGrangerProductDescription').value.length === 0) {
                form.setFieldError('gaborGrangerProductDescription', 'Please enter a product description');
            }
        }

    }

    const formSubmitHandler = async (event) => {
        if (allowUserToRunSurvey === false) return;
        event.preventDefault();
        if (!form.isValid()) {
            highlightFormErrors();
            questionRef.current.scrollIntoView({ behavior: 'smooth' });
            return;
        }
        if (requireAccountToAccessSandbox && numCreditsAvailable < numSurveysInBatch) {
            setOverCreditLimit(true);
            return;
        }
        // Deep copy of form.values
        const oldFormValues = mergeDictionaries(initialFormState, form.values);
        const oldFormValuesDeepCopy = JSON.parse(JSON.stringify(oldFormValues));
        setDefaultFetchError(false);
        setIsRunningNewSurvey(true);
        const userId = currentUser ? currentUser.uid : 'anon';
        const email = currentUser ? currentUser.email : 'anon';
        const inputData = {
            ...oldFormValuesDeepCopy,
            numSurveysInBatch,
            email,
            userId,
            updateUserCredits: requireAccountToAccessSandbox,
            origin: 'survey-form',
        };
        if (analytics) {
            analytics.track({
                userId,
                event: 'Started sandbox survey',
            });
        }
        try {
            const functionString = getFunctionString(form.values.surveyType);
            const results = await fetch(`/.netlify/functions/${functionString}`,
                { method: "POST", body: JSON.stringify(inputData) });
            const json_results = await results.json();
            if (results.ok) {
                let { surveyData, date, id } = json_results;
                // Do this to overwrite any embeddings information that is stored in the form from the previous survey
                const data = { ...oldFormValuesDeepCopy, surveyData, date, id, };
                if (analytics) {
                    analytics.track({
                        userId,
                        event: 'Finished sandbox survey',
                        properties: {
                            ...data
                        }
                    });
                };
                setSurveyResponse(data);
                setUserSurveyHistory([data, ...userSurveyHistory]);
                // 
                // Other requireAccountToAccessSandbox check here
                if (requireAccountToAccessSandbox) updateCreditUsage(numSurveysInBatch);
                // 
                // 
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                if (json_results.error === "Over credit limit" && typeof (json_results['numCreditsAvailable']) === 'number') {
                    setOverCreditLimit(true);
                    updateCreditUsage(json_results['numCreditsAvailable']);
                } else {
                    setDefaultFetchError(true);
                    setIsRunningNewSurvey(false);
                }
            }
        } catch (error) {
            setDefaultFetchError(true);
            setIsRunningNewSurvey(false);
            if (analytics) {
                analytics.track({
                    userId,
                    event: 'Sandbox survey error',
                    properties: {
                        ...inputData,
                        error: error.message
                    }
                });
            }
        }
    }

    const getModalContent = () => {
        const modalText = numCreditsAvailable > 0 ?
            <Text>You don't have enough survey credits to run this set of surveys.
                You can still run <Code sx={{ fontSize: '14px', background: '#e4e4e4' }}>{numCreditsAvailable.toLocaleString()}</Code> survey{numCreditsAvailable > 1 && 's'} in the Simulator.</Text>
            :
            <Text>You don't have any survey credits left. Please contact us at <a href='mailto:support@roundtable.ai' target='_blank'>support@roundtable.ai</a> to buy credits.</Text>
        return modalText;
    }


    // Keep everything the same, but if surveyResponse changes, then set form values to surveyResponse


    useEffect(() => {
        // If no survey response, then set form values to initial form state
        if (surveyResponse.id) {
            const newFormState = mergeDictionaries(initialFormState, surveyResponse);
            form.setValues(newFormState);
            // if (surveyResponse.product !== '' && productsVector.includes(surveyResponse.product) === true) {
            //     updateProducts(surveyResponse.product);
            // }

        } else {
            // form.reset();
            form.setFieldValue('surveyQuestion', '');
            form.setFieldValue('surveyAnswers', ['', '']);
            form.setFieldValue('customQuestionsAndResponses', []);
            form.setFieldValue('surveyDescriptions', []);
            form.setFieldValue('isPanel', false);
            form.setFieldValue('surveyYears', [defaultYear, defaultYear]);
        }
    }, [surveyResponse]);

    const allowUserToRunSurvey = currentUser || requireAccountToAccessSandbox === false;
    
    return (
        <>
            <ResponseConditioningModal
                surveyForm={form}
                conditionModalOpen={conditionModalOpen}
                setConditionModalOpen={setConditionModalOpen}
                modalData={modalData}
                setModalData={setModalData}
            />
            <div className={classes.formWrapper}>
                <div className={classes.innerForm}>
                    <Space h='md' />
                    <form>
                        <Text ref={questionRef} className={classes.surveyFormInnerTitle}>Survey type</Text>
                        <Space h={4} />
                        <Select
                            radius='md'
                            size='md'
                            data={[
                                {
                                    label: `Multiple-choice question`,
                                    value: `MCQ`,
                                    description: 'Run a single multiple-choice question.',
                                },
                                {
                                    label: `Gabor-Granger study`,
                                    value: 'GGS',
                                    description: 'Run a Gabor-Granger pricing study to measure price elasticity and predict revenue.',
                                    labelImage: <Badge color={form.values.surveyType === 'GGS' ? 'white' : 'dark'} variant="outline" size='xs'>Beta</Badge>
                                }
                            ]}
                            itemComponent={DetailedSelect}
                            {...form.getInputProps('surveyType')}
                            onChange={(e) => {
                                if (e === form.values.surveyType) return;
                                form.reset();
                                form.setFieldValue('surveyType', e);
                                // If surveyType is GGS, change to "product"
                                if (e === 'GGS') {
                                    form.setFieldValue('questionType', 'product');
                                }
                            }}
                            icon={form.values.surveyType === "GGS" && <Badge color='dark' variant="outline" size='xs'>Beta</Badge>}
                            iconWidth={form.values.surveyType === "GGS" && '410'}
                            sx={{
                                input: {
                                    paddingLeft: form.values.surveyType === "GGS" && 'calc(2.625rem  / 3) !important'
                                }
                            }}
                        />
                        <Space h='md' />
                        {
                            form.values.surveyType === 'MCQ' ?
                                <>
                                    <Text className={classes.surveyFormInnerTitle}>
                                        Ask a multiple-choice question
                                    </Text>
                                    <Space h={4} />
                                    <Textarea
                                        radius='md'
                                        placeholder={
                                            (form.values.questionType === 'product' && form.values.surveyModel.includes('GSS') === false) ?
                                                "Would you buy this product?"
                                                :
                                                "Do you exercise every day?"
                                        }
                                        size='md'
                                        autosize
                                        {...form.getInputProps('surveyQuestion')}
                                        maxLength='750'
                                    />
                                    <Space h='md' />
                                    <div>
                                        <Text ref={answerRef} className={classes.surveyFormInnerTitle}>
                                            Set the options
                                        </Text>
                                        <Space h={4} />
                                        {
                                            form.getInputProps(`surveyAnswers`).value.map((answer, index) => (
                                                <Fragment key={`answer-${index}`}>
                                                    <Textarea
                                                        radius='md'
                                                        className={classes.inputWithRightSectionMargin}
                                                        placeholder={`Set option ${alphabet[index]}`}
                                                        autosize
                                                        maxLength='100'
                                                        autoFocus={getInputAutoFocus('surveyAnswers', index)}
                                                        {...form.getInputProps(`surveyAnswers.${index}`)}
                                                        size='md'
                                                        onBlur={() => {
                                                            if ((answer) === '' && form.getInputProps(`surveyAnswers`).value.length > 2) {
                                                                removeSurveyItem('surveyAnswers', index);
                                                            }
                                                        }}
                                                        icon={<Text>{alphabet[index]}</Text>}
                                                        rightSection={
                                                            form.getInputProps(`surveyAnswers`).value.length > 2 && (
                                                                <ActionIcon
                                                                    size={32}
                                                                    radius="xl"
                                                                    onMouseDown={() => { removeSurveyItem('surveyAnswers', index) }}
                                                                >
                                                                    <IconX stroke={1.5} size={20} />
                                                                </ActionIcon>
                                                            )
                                                        }
                                                    />
                                                    <Space h='xs' />
                                                </Fragment>
                                            ))
                                        }
                                        {
                                            form.getInputProps(`surveyAnswers`).value.length < 7 &&
                                            (
                                                <Button
                                                    variant='subtle'
                                                    leftIcon={<IconPlus size={20} stroke={1.5} />}
                                                    color='gray'
                                                    radius='md'
                                                    onClick={() => { form.insertListItem('surveyAnswers', '') }}
                                                    style={{ fontWeight: '400' }}
                                                >
                                                    Add an option
                                                </Button>
                                            )
                                        }
                                    </div>
                                </>
                                :
                                <>
                                    <Text className={classes.surveyFormInnerTitle}>Product name</Text>
                                    <Space h={4} />
                                    <Textarea
                                        radius='md'
                                        placeholder="PlayStation 3 Gaming Console"
                                        size='md'
                                        autosize
                                        {...form.getInputProps('gaborGrangerProductName')}
                                        maxLength='750'
                                    />
                                    <Space h='md' />
                                    <Text className={classes.surveyFormInnerTitle}>Product description</Text>
                                    <Space h={4} />
                                    <Textarea
                                        radius='md'
                                        placeholder="The Sony PlayStation 3 is a gaming console that offers high-definition graphics, online gaming capabilities, and Blu-ray disc playback. It features backward compatibility with most PlayStation and PlayStation 2 games, and provides ample storage with a 500GB hard drive."
                                        size='md'
                                        autosize
                                        minRows={9}
                                        {...form.getInputProps('gaborGrangerProductDescription')}
                                        maxLength='1000'
                                    />
                                    <Space h='md' />
                                    <Text className={classes.surveyFormInnerTitle}>Price range</Text>
                                    <Text size='sm' color='gray'>
                                        We automatically estimate demand for 20 equally spaced prices between the min and max price (inclusive).
                                    </Text>
                                    <Space h={4} />
                                    <Group position='center' spacing='xs'>
                                        <NumberInput
                                            radius='md'
                                            size='md'
                                            label="Min price (USD)"
                                            sx={{ width: '125px', flexGrow: 2 }}
                                            hideControls
                                            min={0}
                                            classNames={classes}
                                            {...form.getInputProps(`gaborGrangerMinPrice`)}
                                            step={0.01}
                                            precision={2}
                                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                            onBlur={(e) => {
                                                const numberValue = convertCurrencyStringToNumber(e.target.value);
                                                if (numberValue >= form.values.gaborGrangerMaxPrice) {
                                                    form.setFieldValue('gaborGrangerMaxPrice', numberValue + 1);
                                                } else if (form.values.gaborGrangerMaxPrice - numberValue < 1) {
                                                    form.setFieldValue('gaborGrangerMaxPrice', numberValue + 1);
                                                }
                                                form.setFieldValue('gaborGrangerMinPrice', numberValue);

                                            }}
                                            formatter={(value) =>
                                                !Number.isNaN(parseFloat(value))
                                                    ? `$${parseFloat(value).toFixed(2)}`.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
                                                    : '$0.00'
                                            }
                                        />
                                        <Text
                                            size='xl'
                                            sx={{ flexGrow: 1.5 }}
                                            ta='center'
                                        >
                                            -
                                        </Text>
                                        <NumberInput
                                            radius='md'
                                            size='md'
                                            label="Max price (USD)"
                                            sx={{ width: '125px', flexGrow: 2 }}
                                            hideControls
                                            min={1}
                                            max={1000000}
                                            classNames={classes}
                                            {...form.getInputProps(`gaborGrangerMaxPrice`)}
                                            step={0.01}
                                            precision={2}
                                            onBlur={(e) => {
                                                const numberValue = convertCurrencyStringToNumber(e.target.value);
                                                if (numberValue < 1) {
                                                    form.setFieldValue('gaborGrangerMaxPrice', 1);
                                                    form.setFieldValue('gaborGrangerMinPrice', 0);
                                                    return;
                                                }
                                                if (form.values.gaborGrangerMinPrice >= numberValue) {
                                                    form.setFieldValue('gaborGrangerMinPrice', numberValue - 1);
                                                } else if (numberValue - form.values.gaborGrangerMinPrice < 1) {
                                                    form.setFieldValue('gaborGrangerMinPrice', numberValue - 1);
                                                }
                                                form.setFieldValue('gaborGrangerMaxPrice', numberValue);
                                            }}
                                            formatter={(value) => {
                                                return !Number.isNaN(parseFloat(value))
                                                    ? `$${parseFloat(value).toFixed(2)}`.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
                                                    : '$0.00'
                                            }
                                            }
                                        />
                                    </Group>
                                </>
                        }
                        {
                            form.values.surveyModel.includes('GSS') &&
                            <>
                                <Space h='md' />
                                <Text className={classes.surveyFormInnerTitle}>Describe your participants
                                    <Text component='span' color='gray' size='sm'> (optional)</Text></Text>
                                <Text size='sm' color='gray'>
                                    e.g. "Started eating vegan in the last month"
                                </Text>
                                <Space h={4} />
                                {form.getInputProps(`surveyDescriptions`).value.map((description, index) => (
                                    <Fragment key={`surveyDescriptions-${index}`}>
                                        <Textarea
                                            radius='md'
                                            maxLength='100'
                                            autosize
                                            className={classes.inputWithRightSectionMargin}
                                            autoFocus={getInputAutoFocus('surveyDescriptions', index)}
                                            size='md'
                                            {...form.getInputProps(`surveyDescriptions.${index}`)}
                                            onBlur={() => {
                                                if (description === '') removeSurveyItem('surveyDescriptions', index)
                                            }}
                                            icon={
                                                <ActionIcon
                                                    radius="xs"
                                                    size="xl"
                                                >
                                                    <IconPointFilled stroke={1.5} size={15} />
                                                </ActionIcon>
                                            }
                                            rightSection={
                                                <ActionIcon
                                                    size={32}
                                                    radius="xl"
                                                    onMouseDown={() => { removeSurveyItem('surveyDescriptions', index) }}
                                                >
                                                    <IconX stroke={1.5} size={20} />
                                                </ActionIcon>
                                            }
                                        />
                                        <Space h='xs' />
                                    </Fragment>
                                ))}
                                <Fragment>
                                    {form.getInputProps(`surveyDescriptions`).value.length < 10 &&
                                        <Button
                                            variant='subtle'
                                            leftIcon={<IconPlus size={20} stroke={1.5} />}
                                            color='gray'
                                            radius='md'
                                            onClick={() => { form.insertListItem('surveyDescriptions', '') }}
                                            style={{ fontWeight: '400' }}
                                        >
                                            Add a description
                                        </Button>
                                    }
                                </Fragment>
                            </>
                        }
                        {
                            form.values.surveyType === 'MCQ' &&
                            <>
                                <Space h='md' />
                                <Text className={classes.surveyFormInnerTitle}>Condition responses
                                    <Text component='span' color='gray' size='sm'> (optional)</Text>
                                </Text>
                                <Text size='sm' color='gray'>
                                    e.g. Answered "30" to "How old are you?"
                                </Text>
                                <Space h={4} />
                                <DragNDrop
                                    form={form}
                                    setModalData={setModalData}
                                    setConditionModalOpen={setConditionModalOpen}
                                />
                                <Button
                                    variant='subtle'
                                    leftIcon={<IconPlus size={20} stroke={1.5} />}
                                    color='gray'
                                    radius='md'
                                    onClick={() => { setConditionModalOpen(true) }}
                                    style={{ fontWeight: '400' }}
                                >
                                    Add a conditioning question
                                </Button>
                            </>
                        }
                        {
                            form.values.surveyModel.includes('GSS') && form.values.surveyType === 'MCQ' &&
                            <>
                                <Space h='md' />
                                <Text className={classes.surveyFormInnerTitle}>
                                    {form.values.isPanel ? 'Set the survey years' : 'Set the survey year'}
                                </Text>
                                <Space h={4} />
                                <Group spacing='xs'>
                                    <Switch
                                        label="Run a panel"
                                        size="lg"
                                        sx={{ label: { fontSize: '16px', fontWeight: '400' } }}
                                        {...form.getInputProps('isPanel', { type: 'checkbox' })}
                                        onChange={() => {
                                            const nowIsPanel = !form.getInputProps('isPanel').value;
                                            form.setFieldValue('isPanel', nowIsPanel);
                                            const oldSurveyYears = form.getInputProps('surveyYears').value;
                                            if (nowIsPanel) {
                                                form.setFieldValue('surveyYears.0', firstYear);
                                                const firstYearToAdd = oldSurveyYears[1] > firstYear ? oldSurveyYears[1] : oldSurveyYears[1] + 1;
                                                form.setFieldValue('surveyYears.1', firstYearToAdd);
                                            } else {
                                                const oldEnd = form.getInputProps('surveyYears.1').value;
                                                form.setFieldValue('surveyYears.0', oldEnd);
                                                form.setFieldValue('surveyYears.1', oldEnd);
                                            }
                                        }}
                                    />
                                    <Badge color="gray" variant="outline" size='sm'>Beta</Badge>
                                </Group>
                                <Space h='md' />
                                {
                                    form.values.isPanel ?
                                        <>
                                            <RangeSlider
                                                color="dark"
                                                size='sm'
                                                label={null}
                                                radius="lg"
                                                min={firstYear}
                                                max={lastYear}
                                                step={1}
                                                className={classes.slider}
                                                marks={sliderYearMarks}
                                                {...form.getInputProps(`surveyYears`)}
                                                thumbChildren={<IconGripVertical size={16} stroke={1.5} />}
                                                thumbSize={26}
                                                styles={{
                                                    thumb: { borderWidth: 1 },
                                                    mark: { background: 'none', border: 'none' },
                                                    markLabel: { marginTop: '5px' },
                                                    root: { width: '100% !important' }
                                                }}
                                                minRange={1}
                                            />
                                            <Space h='sm' />
                                            <Group sx={{ marginTop: 25, }} position='apart'>
                                                <NumberInput
                                                    radius='md'
                                                    size='md'
                                                    label="First year"
                                                    hideControls
                                                    styles={{ input: { width: '125px' } }}
                                                    min={firstYear}
                                                    max={lastYear - 1}
                                                    classNames={classes}
                                                    {...form.getInputProps(`surveyYears.0`)}
                                                    onChange={(year) => {
                                                        const higherValue = form.getInputProps(`surveyYears.1`).value;
                                                        if (year > higherValue || year === undefined) {
                                                            form.setFieldValue('surveyYears.0', higherValue);
                                                        } else {
                                                            form.setFieldValue('surveyYears.0', year);
                                                        }
                                                    }}
                                                    onBlur={(event) => {
                                                        if (event.target.value === '' || isNaN(event.target.value) || event.target.value < firstYear || event.target.value > (lastYear - 1)) {
                                                            form.setFieldValue('surveyYears.0', firstYear);
                                                        } else {
                                                            const surveyYears = form.getInputProps('surveyYears').value;
                                                            if (event.target.value >= surveyYears[1]) {
                                                                const newYear = Math.min(event.target.value, lastYear - 1);
                                                                form.setFieldValue('surveyYears', [newYear, lastYear]);
                                                            }
                                                        }
                                                    }}
                                                />
                                                <Text size='xl'>-</Text>
                                                <NumberInput
                                                    radius='md'
                                                    size='md'
                                                    styles={{ input: { width: '125px' } }}
                                                    label="Last year"
                                                    hideControls
                                                    min={firstYear + 1}
                                                    max={lastYear}
                                                    classNames={classes}
                                                    {...form.getInputProps(`surveyYears.1`)}
                                                    onChange={(year) => {
                                                        const lowerValue = form.getInputProps(`surveyYears.0`).value;
                                                        if (year < lowerValue || year === undefined) {
                                                            form.setFieldValue('surveyYears.1', lowerValue);
                                                        } else {
                                                            form.setFieldValue('surveyYears.1', year);
                                                        }
                                                    }}
                                                    onBlur={(event) => {
                                                        if (event.target.value === '' || isNaN(event.target.value) || event.target.value < (firstYear + 1) || event.target.value > lastYear) {
                                                            form.setFieldValue('surveyYears.1', lastYear);
                                                        } else {
                                                            const surveyYears = form.getInputProps('surveyYears').value;
                                                            if (event.target.value <= surveyYears[0]) {
                                                                const newYear = Math.max(event.target.value, firstYear + 1);
                                                                form.setFieldValue('surveyYears', [firstYear, newYear]);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Group>
                                        </>
                                        :
                                        <>
                                            <Slider
                                                label={null}
                                                radius="lg"
                                                defaultValue={defaultYear}
                                                min={firstYear}
                                                max={lastYear}
                                                step={1}
                                                marks={sliderYearMarks}
                                                {...form.getInputProps('surveyYears.1')}
                                                size='sm'
                                                color='dark'
                                                thumbChildren={<IconGripVertical size={16} stroke={1.5} />}
                                                thumbSize={26}
                                                className={classes.slider}
                                                onChange={(year) => {
                                                    form.setFieldValue('surveyYears', [year, year]);
                                                }}
                                                styles={{
                                                    thumb: { borderWidth: 1 },
                                                    mark: { background: 'none' },
                                                    markLabel: { marginTop: '5px' },
                                                    root: { width: '100% !important' }
                                                }}
                                            />
                                            <Space h='sm' />
                                            <Group sx={{ marginTop: 25 }}>
                                                <NumberInput
                                                    radius='md'
                                                    styles={{ input: { width: '125px' } }}
                                                    size='md'
                                                    style={{ width: '40%' }}
                                                    defaultValue={defaultYear}
                                                    label="Survey year"
                                                    hideControls
                                                    min={firstYear}
                                                    max={lastYear}
                                                    classNames={classes}
                                                    {...form.getInputProps('surveyYears.1')}
                                                    onChange={(year) => {
                                                        form.setFieldValue('surveyYears', [year, year]);
                                                    }}
                                                    onBlur={(event) => {
                                                        if (event.target.value === '' || isNaN(event.target.value) || event.target.value < firstYear || event.target.value > lastYear) {
                                                            form.setFieldValue('surveyYears', [defaultYear, defaultYear]);
                                                        }
                                                    }}
                                                />
                                            </Group>
                                        </>
                                }
                            </>
                        }
                        <Space h='sm' />
                        <Divider my="sm" />
                        {
                            defaultFetchError ?
                                <>
                                    <Space h='md' />
                                    <Alert
                                        title="Error"
                                        color="red"
                                        radius='md'
                                    >
                                        Our request to OpenAI has timed out. Please wait two minutes before trying again, or <a href='mailto:support@roundtable.ai' target='_blank'>contact support</a>.
                                    </Alert>
                                    <Space h='md' />
                                </> :
                                <>
                                    <Space h='sm' />
                                </>
                        }
                        <Group position='apart' spacing='xs'>
                            <Button
                                size='lg'
                                radius='md'
                                disabled={isRunningNewSurvey}
                                loading={isRunningNewSurvey}
                                onClick={formSubmitHandler}
                                component={allowUserToRunSurvey ? Button : Link}
                                to='/sign-up'
                                sx={{
                                    width: allowUserToRunSurvey ? '225px' : '100%',
                                    padding: '0px'
                                }}
                            >
                                {getSubmitButtonText(allowUserToRunSurvey, form.values.isPanel, form.values.surveyType, isRunningNewSurvey)}
                            </Button>
                            {
                                allowUserToRunSurvey &&
                                <Button
                                    variant='subtle'
                                    size='sm'
                                    radius='md'
                                    onClick={() => {
                                        setSurveyResponse({});
                                        // Update form to initial values
                                        form.reset();
                                    }}
                                    sx={{
                                        width: '55px',
                                        padding: '0px',
                                        // background: 'pink',
                                    }}

                                    // compact
                                    hidden={allowUserToRunSurvey === false}
                                >
                                    Reset
                                </Button>

                            }
                        </Group>
                        <Space h='xs' />
                        {
                            form.values.surveyModel.includes('GSS') ?
                                <>
                                    <Text color='gray' size='sm'>
                                        All estimates are for representative samples in the United States.
                                    </Text>
                                    <Space h='xs' />
                                </>
                                :
                                <>
                                    <Text color='gray' size='sm'>
                                        All estimates are for representative samples based on your training data.
                                    </Text>
                                    <Space h='xs' />
                                </>
                        }
                    </form>
                    <Modal
                        centered
                        opened={overCreditLimit}
                        onClose={() => { setOverCreditLimit(false) }}
                        title="Survey limit reached"
                        size='md'
                        radius='lg'
                        className={classes.modalContent}
                    >
                        {getModalContent()}
                    </Modal>
                </div>
            </div>
        </>
    )
}

export default SurveyForm;